import React, { useState, useEffect } from "react";
import { Kanban } from "react-kanban-kit";
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { getTaskStatuses, reorderTask } from "../../Requests/TaskRequests";
import { getTeamUuid } from "../../utils/cookies";
import TaskCard from "./TaskCard";
import TaskForm from "./TaskForm";

export default function TaskKanbanBoard({ onClose, tasks, onTasksUpdate, statuses: propStatuses, categories: propCategories, isFullPage = false }) {
  const { t } = useTranslation();
  const [statuses, setStatuses] = useState(propStatuses || []);
  const [loading, setLoading] = useState(!propStatuses);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedStatusId, setSelectedStatusId] = useState(null);

  useEffect(() => {
    if (propStatuses) {
      setStatuses(propStatuses);
      setLoading(false);
    } else {
      loadStatuses();
    }
  }, [propStatuses]);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const teamUuid = getTeamUuid();
      const response = await getTaskStatuses(teamUuid);
      setStatuses(response.data || []);
    } catch (err) {
      console.error("Error loading statuses:", err);
    } finally {
      setLoading(false);
    }
  };

  const transformToKanbanData = () => {
    if (!statuses.length) return { root: { id: "root", title: "Root", children: [], totalChildrenCount: 0, parentId: null } };

    const kanbanData = {
      root: {
        id: "root",
        title: "Root",
        children: statuses.map(status => `status-${status.id}`),
        totalChildrenCount: statuses.length,
        parentId: null,
      }
    };

    // Add status columns
    statuses.forEach(status => {
      const statusTasks = tasks.filter(task => task.status_id === status.id);
      const taskIds = statusTasks
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(task => `task-${task.id}`);

      kanbanData[`status-${status.id}`] = {
        id: `status-${status.id}`,
        title: status.name,
        children: taskIds,
        totalChildrenCount: taskIds.length,
        parentId: "root",
        content: {
          status,
          color: status.color || '#6B7280'
        }
      };

      // Add tasks as cards
      statusTasks.forEach(task => {
        kanbanData[`task-${task.id}`] = {
          id: `task-${task.id}`,
          title: task.title,
          parentId: `status-${status.id}`,
          children: [],
          totalChildrenCount: 0,
          type: "task",
          content: task
        };
      });
    });

    return kanbanData;
  };

  const handleCardMove = async (move) => {
    try {
      const taskId = parseInt(move.cardId.replace('task-', ''));
      const targetStatusId = parseInt(move.toColumnId.replace('status-', ''));
      
      // Update the task order on the backend
      await reorderTask(taskId, targetStatusId, move.position);
      
      // Update local state
      const updatedTasks = tasks.map(t => {
        if (t.id === taskId) {
          return { ...t, status_id: targetStatusId, sort_order: move.position };
        }
        return t;
      });
      
      onTasksUpdate(updatedTasks);
    } catch (error) {
      console.error("Error moving task:", error);
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskForm(true);
  };

  const handleAddTask = (statusId) => {
    setSelectedTask(null);
    setSelectedStatusId(statusId);
    setShowTaskForm(true);
  };

  const handleTaskUpdate = (updatedTask) => {
    if (selectedTask) {
      // Update existing task
      const updatedTasks = tasks.map(t => 
        t.id === updatedTask.id ? updatedTask : t
      );
      onTasksUpdate(updatedTasks);
    } else {
      // Add new task
      onTasksUpdate([...tasks, updatedTask]);
    }
    setShowTaskForm(false);
    setSelectedTask(null);
    setSelectedStatusId(null);
  };

  const handleTaskDelete = (taskToDelete) => {
    // Remove task from the list
    const updatedTasks = tasks.filter(t => t.id !== taskToDelete.id);
    onTasksUpdate(updatedTasks);
    
    // Close the form
    setShowTaskForm(false);
    setSelectedTask(null);
    setSelectedStatusId(null);
  };

  const configMap = {
    task: {
      render: ({ data }) => (
        <div 
          onClick={() => handleTaskClick(data.content)}
          className="cursor-pointer"
        >
          <TaskCard 
            task={data.content} 
            onEdit={handleTaskClick}
          />
        </div>
      ),
      isDraggable: true,
    }
  };

  const renderColumnHeader = ({ data }) => {
    const status = data.content?.status;
    if (!status) return null;

    return (
      <div 
        className="p-4 border-b border-gray-200" 
        style={{ 
          backgroundColor: "#ffffff !important",
          background: "#ffffff !important"
        }}
      >
        <div 
          className="flex items-center justify-between"
          style={{ 
            backgroundColor: "#ffffff !important",
            background: "#ffffff !important"
          }}
        >
          <div 
            className="flex items-center gap-2"
            style={{ 
              backgroundColor: "#ffffff !important",
              background: "#ffffff !important"
            }}
          >
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: data.content.color }}
            />
            <h3 
              className="font-medium text-gray-900"
              style={{ 
                backgroundColor: "transparent !important",
                background: "transparent !important"
              }}
            >
              {status.name}
            </h3>
            <span 
              className="text-gray-600 text-xs px-2 py-1 rounded-full border border-gray-300"
              style={{ 
                backgroundColor: "#ffffff !important",
                background: "#ffffff !important"
              }}
            >
              {data.children.length}
            </span>
          </div>
          <button
            onClick={() => handleAddTask(status.id)}
            className="p-1 hover:bg-iavisus-100 hover:bg-opacity-20 rounded transition-colors"
            style={{ 
              backgroundColor: "transparent !important"
            }}
          >
            <PlusIcon className="w-4 h-4 text-iavisus-600" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    const containerClass = isFullPage 
      ? "w-full h-64 flex items-center justify-center"
      : "fixed inset-0 bg-white z-50 flex items-center justify-center";
    
    return (
      <div className={containerClass}>
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  const kanbanData = transformToKanbanData();

  const containerClass = isFullPage 
    ? "w-full overflow-hidden"
    : "fixed inset-0 bg-white z-50 overflow-hidden";

  const headerContent = (
    <div className="bg-white border-b border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {t('Tasks.KanbanBoard')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('Tasks.KanbanSubtitle')}
          </p>
        </div>
        {!isFullPage && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-iavisus-100 hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-iavisus-600" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className={containerClass}>
      {/* Header - only show in modal mode, not full page */}
      {!isFullPage && headerContent}

      {/* Kanban Board */}
      <div className={isFullPage ? "w-full overflow-auto h-full" : "flex-1 overflow-hidden p-4"}>
        <div className={isFullPage ? "h-full" : "h-full"}>
          <Kanban
            dataSource={kanbanData}
            configMap={configMap}
            onCardMove={handleCardMove}
            columnHeaderRenderer={renderColumnHeader}
            className="h-full"
            rootStyle={{ 
              minHeight: "100%", 
              height: "100%",
              width: "100%",
              display: "flex"
            }}
            rootClassName="h-full w-full"
            columnWrapperStyle={(column) => ({
              minHeight: "calc(100vh - 250px)",
              height: "100%",
              backgroundColor: "#ffffff !important",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              flex: "1 1 0px",
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: "0px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              margin: "0 4px"
            })}
            columnStyle={(column) => ({
              height: "100%",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              backgroundColor: "#ffffff"
            })}
            columnListContentStyle={(column) => ({
              flex: 1,
              padding: "12px",
              overflowY: "auto",
              backgroundColor: "#ffffff"
            })}
          />
        </div>
      </div>


      {/* Task Form Drawer */}
      <TaskForm
        open={showTaskForm}
        task={selectedTask}
        statusId={selectedStatusId}
        statuses={statuses}
        categories={propCategories}
        onSave={handleTaskUpdate}
        onDelete={handleTaskDelete}
        onCancel={() => {
          setShowTaskForm(false);
          setSelectedTask(null);
          setSelectedStatusId(null);
        }}
      />
    </div>
  );
}